<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'

const props = defineProps<{
  total: number
  modelValue: number
  container?: HTMLElement | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const displayDots = computed(() => {
  const total = props.total;
  if (total === 0) return [];

  let maxToShow = 5;
  const currentIndex = props.modelValue;

  if (currentIndex === 0 || currentIndex === total - 1) {
    maxToShow = Math.min(total, 3);
  } else if (currentIndex === 1 || currentIndex === total - 2) {
    maxToShow = Math.min(total, 4);
  } else {
    maxToShow = Math.min(total, 5);
  }

  let start = currentIndex - Math.floor(maxToShow / 2);
  if (start < 0) start = 0;
  if (start + maxToShow > total) start = total - maxToShow;

  const dots = [];
  for (let i = 0; i < maxToShow; i++) {
    if (start + i < total) {
      dots.push(start + i);
    }
  }
  return dots;
});

const prev = () => {
  if (props.modelValue > 0) {
    emit('update:modelValue', props.modelValue - 1)
  }
}

const next = () => {
  if (props.modelValue < props.total - 1) {
    emit('update:modelValue', props.modelValue + 1)
  }
}

const goTo = (index: number) => {
  emit('update:modelValue', index)
}

const isOverflowing = ref(true)

const checkOverflow = () => {
  if (props.container) {
    isOverflowing.value = props.container.scrollWidth > props.container.clientWidth
  }
}

let resizeObserver: ResizeObserver | null = null

watch(() => props.container, (newContainer) => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (newContainer && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })
    resizeObserver.observe(newContainer)
  }
  checkOverflow()
}, {immediate: true})

watch(() => [props.total, props.modelValue], () => {
  nextTick(checkOverflow)
})

onMounted(() => {
  window.addEventListener('resize', checkOverflow)
  nextTick(checkOverflow)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkOverflow)
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<template>
  <div v-if="total > 1 && isOverflowing" class="hidden lg:flex items-center justify-center gap-4 pt-2 select-none">
    <!-- Left Arrow -->
    <button
        @click="prev"
        :disabled="modelValue === 0"
        class="p-1 text-gray-400 hover:text-black disabled:opacity-20 transition-all duration-300 hover:scale-110 active:scale-95"
        type="button"
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"
           class="w-3 h-3">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
      </svg>
    </button>

    <!-- Dots -->
    <div class="flex items-center space-x-1.5">
      <button
          v-for="i in displayDots"
          :key="i"
          class="w-1.5 h-1.5 rounded-full shadow-sm transition-all duration-300"
          :class="i === modelValue ? 'bg-black ring-2 ring-gray-300 scale-125' : 'bg-black/30 hover:bg-black/50'"
          @click="goTo(i)"
          type="button"
      />
    </div>

    <!-- Right Arrow -->
    <button
        @click="next"
        :disabled="modelValue === total - 1"
        class="p-1 text-gray-400 hover:text-black disabled:opacity-20 transition-all duration-300 hover:scale-110 active:scale-95"
        type="button"
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"
           class="w-3 h-3">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
      </svg>
    </button>
  </div>
</template>

<style scoped>
</style>
